import { Search, MapPin, Sparkles, TrendingUp, ArrowRight, ShieldCheck, Lock, Users, LocateFixed, Loader2 } from 'lucide-react';
import type { HeroProps } from '../types';
import { CITIES, QUICK_TAGS } from '../data';
import { useLocation } from '../hooks/useLocation';
import { useEffect, useState } from 'react';

export default function Hero({ searchQuery, onSearchChange }: HeroProps): JSX.Element {
  const { country, city, isLoading, error, requestLocation } = useLocation();
  const [selectedCity, setSelectedCity] = useState('All Pakistan');

  useEffect(() => {
    if (city && CITIES.includes(city)) {
      setSelectedCity(city);
    }
  }, [city]);

  return (
    <section
      className="hero-section relative overflow-hidden min-h-[90vh] flex items-center"
      aria-labelledby="hero-heading"
    >
      {/* Animated mesh gradient background */}
      <div className="absolute inset-0 gradient-mesh" />
      
      {/* Animated blob backgrounds */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          aria-hidden="true"
          className="blob blob-1 absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ 
            background: 'radial-gradient(circle, rgba(59,111,255,0.4) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <div
          aria-hidden="true"
          className="blob blob-2 absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full"
          style={{ 
            background: 'radial-gradient(circle, rgba(236,72,153,0.35) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <div
          aria-hidden="true"
          className="blob blob-3 absolute -bottom-40 left-1/3 w-[600px] h-[600px] rounded-full"
          style={{ 
            background: 'radial-gradient(circle, rgba(6,182,212,0.3) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <div
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-20"
          style={{ 
            background: 'radial-gradient(circle, rgba(168,85,247,0.3) 0%, transparent 60%)',
            filter: 'blur(80px)',
            animation: 'float 12s ease-in-out infinite'
          }}
        />
      </div>

      {/* Animated grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto text-center">
          {/* Animated Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-8 fade-up hover:bg-white/15 transition-all duration-300 cursor-default group">
            <Sparkles className="w-4 h-4 text-warning group-hover:rotate-12 transition-transform" />
            <span className="text-sm font-medium text-white/90">
              Simple & Secure Marketplace
            </span>
            <span className="flex h-2 w-2 rounded-full bg-success animate-pulse" />
          </div>

          {/* Main heading with animated gradient */}
          <h1
            id="hero-heading"
            className="hero-title text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 tracking-tight fade-up"
            style={{ animationDelay: '0.1s' }}
          >
            Buy & Sell{' '}
            <span className="gradient-text-animated">
              Locally
            </span>
          </h1>

          <p 
            className="hero-sub text-lg sm:text-xl text-white/70 mb-10 leading-relaxed max-w-2xl mx-auto fade-up"
            style={{ animationDelay: '0.2s' }}
          >
            {city && country
                ? `Connect with buyers and sellers in ${city}, ${country}. Find cars, property, electronics, and more — all in one place.`
                : country
                  ? `Connect with buyers and sellers in ${country}. Find cars, property, electronics, and more — all in one place.`
                  : 'Connect with buyers and sellers in your area. Find cars, property, electronics, and more — all in one place.'
            }
          </p>

          {/* Modern Search bar with glow effect */}
          <div
            role="search"
            aria-label="Search listings"
            className="search-row max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl shadow-black/30 p-2 fade-up hover-glow transition-all duration-500"
            style={{ animationDelay: '0.3s' }}
          >
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="search-wrap flex-1 flex items-center gap-3 bg-dark-50 rounded-xl px-4 py-3.5 border border-transparent focus-within:border-primary-300 transition-all">
                <Search className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <label htmlFor="hero-search" className="sr-only">Search listings</label>
                <input
                  id="hero-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="What are you looking for?"
                  aria-label="Search listings"
                  className="flex-1 bg-transparent border-none outline-none text-dark-800 placeholder-dark-400 text-base"
                />
              </div>

              <div className="flex items-center gap-2 sm:border-l sm:border-dark-200 sm:pl-2">
                <label htmlFor="city-select" className="sr-only">Select city</label>
                <div className="relative flex-1 sm:flex-none">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500" />
                  <select
                    id="city-select"
                    value={selectedCity}
                    onChange={(event) => setSelectedCity(event.target.value)}
                    className="w-full sm:w-44 appearance-none bg-dark-50 border border-transparent rounded-xl pl-10 pr-8 py-3.5 text-sm text-dark-700 cursor-pointer outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  >
                    {CITIES.map((availableCity) => <option key={availableCity}>{availableCity}</option>)}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-primary flex items-center justify-center gap-2 px-8 py-3.5 text-base font-semibold whitespace-nowrap shine"
                  aria-label="Submit search"
                >
                  <Search className="w-5 h-5 sm:hidden" />
                  <span className="hidden sm:inline">Search</span>
                  <ArrowRight className="w-4 h-4 sm:hidden" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex flex-col items-center gap-1 text-sm text-white/75 sm:flex-row sm:justify-center sm:gap-3">
              <button
                type="button"
                onClick={() => void requestLocation()}
                disabled={isLoading}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors hover:text-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900 disabled:cursor-wait disabled:opacity-70"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
                {isLoading ? 'Finding your city...' : 'Use my location'}
              </button>
              <span>Only used to select a nearby city for your search.</span>
            </div>
            <p className="sr-only" role="status" aria-live="polite">
              {isLoading ? 'Requesting your location.' : error || (city ? `Selected city: ${city}.` : '')}
            </p>
          </div>

          {/* Quick tags with hover effects */}
          <div
            className="quick-tags mt-6 flex flex-wrap items-center justify-center gap-2 fade-up"
            style={{ animationDelay: '0.4s' }}
            aria-label="Popular searches"
          >
            <span className="text-sm text-white/50 flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-warning" />
              Trending:
            </span>
            {QUICK_TAGS.map((tag, index) => (
              <button
                key={tag}
                type="button"
                aria-label={`Search for ${tag}`}
                className="text-sm text-white/80 px-4 py-2 rounded-full border border-white/20 hover:bg-white/15 hover:border-white/30 hover:text-white hover:-translate-y-0.5 transition-all duration-300"
                style={{ animationDelay: `${0.5 + index * 0.05}s` }}
              >
                {tag}
              </button>
            ))}
          </div>

          {/* Trust indicators — professional, uniform pill style */}
          <div
            className="mt-12 flex flex-wrap items-center justify-center gap-3 sm:gap-4 fade-up"
            style={{ animationDelay: '0.6s' }}
            aria-label="Marketplace trust signals"
          >
            <div className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-md px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-white/10 hover:border-white/25 transition-all">
              <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-primary-500/15 ring-1 ring-primary-400/30">
                <Users className="w-3.5 h-3.5 text-primary-300" aria-hidden="true" />
                <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                </span>
              </span>
              <span className="font-medium tracking-tight">Active Community</span>
            </div>

            <div className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-md px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-white/10 hover:border-white/25 transition-all">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-success/15 ring-1 ring-success/30">
                <ShieldCheck className="w-3.5 h-3.5 text-success" aria-hidden="true" />
              </span>
              <span className="font-medium tracking-tight">Verified Sellers</span>
            </div>

            <div className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-md px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-white/10 hover:border-white/25 transition-all">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-warning/15 ring-1 ring-warning/30">
                <Lock className="w-3.5 h-3.5 text-warning" aria-hidden="true" />
              </span>
              <span className="font-medium tracking-tight">Secure Transactions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-surface to-transparent" />
    </section>
  );
}
